import { ChevronDownIcon, ChevronUpIcon } from '@/components/icons';
import { useAccessibilityTTS, useTickSoundInteraction } from '@/hooks';
import { useConfigStore } from '@/stores/configStore';
import { stripAngleBlocks } from '@/utils/faq';
import type { OutputItem } from '@/utils/faqs';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';

interface DropdownFilterProps {
    data: OutputItem[];
    onQuestionSelect: (question: string, answer: string) => void;
}

export default function DropdownFilter({ data, onQuestionSelect }: DropdownFilterProps) {
    const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
    const [activeButton, setActiveButton] = useState<string | null>(null);
    const firstQuestionRefs = useRef<{ [sectionId: string]: HTMLButtonElement | null }>({});
    const { t } = useTranslation();

    const language = useConfigStore((state) => state.config.ui.language);
    const { handleInteraction } = useTickSoundInteraction();
    // Custom TTS handling
    const { speak } = useAccessibilityTTS();

    const speakQuestionClick = (answer: string) => {
        speak(`${answer}`);
    };

    const speakQuestionFocus = (text: string, buttonKey: string) => {
        const isActive = isButtonActive(buttonKey);
        speak(`${isActive ? t('common.button.tts.status_selected') : t('common.button.tts.status_not_selected')} ${text} ${t('common.button.tts.button')}`);
    };

    const speakDropdownClick = (title: string, sectionId: string) => {
        const wasExpanded = isSectionExpanded(sectionId);
        toggleSection(sectionId);

        handleInteraction('', () => { });

        speak(`${title} ${t('common.button.tts.button')}`);

        // Focus on the first question item when expanding the dropdown
        if (!wasExpanded) {
            setTimeout(() => {
                const firstQuestionButton = firstQuestionRefs.current[sectionId];
                if (firstQuestionButton) {
                    firstQuestionButton.focus();
                }
            }, 100);
        }
    };

    const speakDropdownFocus = (text: string) => {
        speak(`${text} ${t('common.button.tts.button')}`);
    };

    const toggleSection = useCallback((sectionId: string) => {
        setExpandedSections((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(sectionId)) {
                newSet.delete(sectionId);
            } else {
                newSet.add(sectionId);
            }
            return newSet;
        });
    }, []);

    const handleButtonClick = useCallback(
        (buttonKey: string, question: string, answer: string) => {
            const formattedAnswer = stripAngleBlocks(answer);
            setActiveButton(buttonKey);
            onQuestionSelect(question, answer);
            speakQuestionClick(formattedAnswer);
        },
        [onQuestionSelect]
    );

    const filteredData = useMemo(
        () =>
            data.map((section) => ({
                ...section,
                translations: section.translations?.filter((translation) => translation.locale === language) || [],
                title: t(`faq.dropdown.${section.key.toLowerCase()}`),
            })),
        [data, language]
    );

    const getButtonKey = (sectionId: string, translationIndex: number, questionIndex: number) => `${sectionId}-${translationIndex}-${questionIndex}`;

    const isSectionExpanded = (sectionId: string) => expandedSections.has(sectionId);
    const isButtonActive = (buttonKey: string) => activeButton === buttonKey;

    return (
        <div className={`${styles['dropdown-container']} scrollbar-custom-gray-99938f scrollbar-w-4`}>
            <div className={styles['dropdown-wrapper']}>
                {filteredData.map((section) => (
                    <div
                        key={section.id}
                        className={`${styles['section-item']} ${isSectionExpanded(section.id) ? styles['section-item-expanded'] : styles['section-item-default']}`}
                        data-region-nav
                    >
                        <button
                            onFocus={() => speakDropdownFocus(section.title)}
                            type="button"
                            onClick={() => speakDropdownClick(section.title, section.id)}
                            className={styles['section-header'] + ' focus-border' + (isSectionExpanded(section.id) ? ' pb-[calc(28*var(--vh-unit))]' : '')}
                        >
                            <div
                                className={`${styles['section-title']} ${isSectionExpanded(section.id) ? styles['section-title-expanded'] : styles['section-title-default']
                                    }`}
                            >
                                {section.title}
                            </div>
                            <div className="size-[calc(24*var(--vh-unit))]">
                                {isSectionExpanded(section.id) ? (
                                    <ChevronUpIcon className={styles['icon-expanded']} />
                                ) : (
                                    <ChevronDownIcon className={styles['icon-default']} />
                                )}
                            </div>
                        </button>

                        {isSectionExpanded(section.id) && section.translations && (
                            <div
                                className={`${styles['section-container']} ${isSectionExpanded(section.id) ? styles['section-container-expanded'] : styles['section-container-default']
                                    }`}
                            >
                                {section.translations.flatMap((translation, translationIndex) =>
                                    translation.questions.map((question, questionIndex) => {
                                        const buttonKey = getButtonKey(section.id, translationIndex, questionIndex);
                                        const answer = translation.answers[questionIndex] || '';
                                        const isFirstQuestion = translationIndex === 0 && questionIndex === 0;

                                        return (
                                            <button
                                                ref={(el) => {
                                                    if (isFirstQuestion) {
                                                        firstQuestionRefs.current[section.id] = el;
                                                    }
                                                }}
                                                onFocus={() => speakQuestionFocus(question, buttonKey)}
                                                key={buttonKey}
                                                onClick={() => handleButtonClick(buttonKey, question, answer)}
                                                className={`${styles['question-button-base']} ${isButtonActive(buttonKey) ? styles['question-button-active'] : styles['question-button-default']
                                                    }`}
                                            >
                                                {question}
                                            </button>
                                        );
                                    })
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
